import { useState, type FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { AddressSearchFieldAutocomplete } from 'components/atoms/Form/AddressSearchField';
import { Button } from 'components/atoms/Button';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import type { BankingSignupResidencyFormValues } from '../constants/onboarding.types';
import type { ActivationStepProps } from '../../ActivationFlow/types';
import useCountryAlpha3Options from 'hooks/shared/useCountryAlpha3Options';
import { ResidencyDetailsSchema } from '../../utils/validation';

interface ResidencyDetailsStepProps extends ActivationStepProps {
  defaultValues?: {
    residencyCountry?: string;
    residencyStreet?: string;
    residencyCity?: string;
    residencyState?: string;
    residencyZip?: string;
  };
  onConfirm: (values: BankingSignupResidencyFormValues) => void;
  isConfirming?: boolean;
}

const ResidencyDetailsStep: FC<ResidencyDetailsStepProps> = ({
  onConfirm,
  defaultValues,
  isConfirming,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  showBackButton,
  onBack,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const { options: countryOptions, alpha2ToAlpha3 } = useCountryAlpha3Options();

  const methods = useForm<BankingSignupResidencyFormValues>({
    defaultValues: defaultValues ?? {
      residencyCountry: '',
      residencyStreet: '',
      residencyCity: '',
      residencyZip: '',
    },
    resolver: joiResolver(ResidencyDetailsSchema(), {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  // todo: consider remove any type
  const setAndValidate = (name: any, value: any, options?: any) =>
    methods.setValue(name, value, { shouldValidate: true, ...options });

  const hasExistingAddress = !!(
    defaultValues?.residencyStreet ||
    defaultValues?.residencyCity ||
    defaultValues?.residencyZip
  );

  const [addressExpanded, setAddressExpanded] = useState(hasExistingAddress);

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      showBackButton={showBackButton}
      onBack={onBack}
      title={title}
      subtitle={subtitle}
      icon={icon}
    >
      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit((values) => onConfirm(values))}
        >
          {addressExpanded ? (
            <>
              <FieldConnector
                className="mt-3"
                name="residencyCountry"
                label={t('residency_country')}
              >
                {({ field, name }) => (
                  <Select
                    {...field}
                    name={name}
                    placeholder={t('residency_country')}
                    options={countryOptions}
                    isSearchable
                    value={field.value || undefined}
                    // todo: consider remove any type
                    onChange={({ value }: any) => field.onChange(value)}
                  />
                )}
              </FieldConnector>

              <FieldConnector
                className="mt-3"
                name="residencyStreet"
                label={t('address.street')}
              >
                {({ field }) => (
                  <FreeTextInput {...field} placeholder={t('address.street')} />
                )}
              </FieldConnector>

              <div className="flex space-x-3 mt-3">
                <FieldConnector
                  className="flex-1"
                  name="residencyZip"
                  label={t('address.postal_code')}
                >
                  {({ field }) => (
                    <FreeTextInput
                      {...field}
                      placeholder={t('address.postal_code')}
                    />
                  )}
                </FieldConnector>

                <FieldConnector
                  className="flex-1"
                  name="residencyCity"
                  label={t('address.city')}
                >
                  {({ field }) => (
                    <FreeTextInput {...field} placeholder={t('address.city')} />
                  )}
                </FieldConnector>
              </div>
            </>
          ) : (
            <AddressSearchFieldAutocomplete
              label={t('address.enter_your_address')}
              searchPlaceholder={t('search')}
              onManualEntry={(streetQuery) => {
                if (streetQuery) {
                  setAndValidate('residencyStreet', streetQuery, {
                    shouldDirty: true,
                  });
                }
                setAddressExpanded(true);
              }}
              onAddressSelect={(address) => {
                if (address.street) {
                  setAndValidate('residencyStreet', address.street, {
                    shouldDirty: true,
                  });
                }
                if (address.city) {
                  setAndValidate('residencyCity', address.city, {
                    shouldDirty: true,
                  });
                }
                if (address.zip) {
                  setAndValidate('residencyZip', address.zip, {
                    shouldDirty: true,
                  });
                }
                const alpha3 = alpha2ToAlpha3(address.country);
                if (alpha3) {
                  setAndValidate('residencyCountry', alpha3, {
                    shouldDirty: true,
                  });
                }
                setAddressExpanded(true);
              }}
            />
          )}

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
              loading={isConfirming}
              disabled={isUpdatingStep || !addressExpanded}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </OnboardingStepWrapper>
  );
};

export default ResidencyDetailsStep;
